import { Notification } from '@awork/framework/models/notification.model'
import { Task } from '@awork/features/task/models/task.model'
import type { User } from '@awork/features/user/models/user.model'
import type { Project } from '@awork/features/project/models/project.model'
import type { Icon } from '@awork/_shared/static-data/icons'
import type { LucideIcon } from '@awork/_shared/directives/icons/types'
import { differenceInMilliseconds } from '@awork/_shared/functions/date-fns-wrappers'
import replace from '@awork/_shared/functions/replace'

export type NotificationView = 'inbox' | 'notifications'
type NotificationCategory = Exclude<Notification['groupType'], 'unseen' | 'seen' | 'default'> | 'conversations'
type NotificationTranslations = typeof q.translations.Notifications
interface SectionHeading {
  id: string
  label: string
  icon: Icon | LucideIcon
  project?: Project
}

export interface NotificationRow {
  notification: Notification
  groupId?: string
  groupCount?: number
  expanded?: boolean
  /** Row position inside an expanded conversation, used to draw one box around it. */
  groupPosition?: 'first' | 'middle' | 'last'
  actionNotifications: Notification[]
}

export interface NotificationSection extends SectionHeading {
  markReadLabel: string
  startIndex: number
  notifications: Notification[]
  rows: NotificationRow[]
  pending: boolean
  hasUnseen: boolean
}

const CATEGORY_ICONS = {
  conversations: 'message-circle',
  deadlines: 'calendar-fold',
  newProjects: 'folder',
  newTasks: 'task',
  newDocuments: 'file-text',
  newDocumentSpaces: 'library',
  newFilters: 'filter',
  newSharedAgents: 'agent',
  newSharedThreads: 'messages-square',
  unassignedTasks: 'user-minus',
  privateTaskUpdates: 'lock-keyhole',
  projectUpdates: 'folder',
  reminders: 'bell',
  taskPredecessorDone: 'git-compare-arrows',
  birthdays: 'cake'
} satisfies Record<NotificationCategory, Icon | LucideIcon>

/**
 * Checks both server and optimistic read state.
 * @param {Notification} notification Message to check.
 * @returns {boolean} Whether the message is unread.
 */
export function isUnread(notification: Notification): boolean {
  return !notification.seen && !notification.seenOn
}

/**
 * Counts unread messages before applying the active search.
 * @param {readonly Notification[]} notifications Available notifications.
 * @returns {Record<NotificationView, number>} Unread counts for each view.
 */
export function getUnreadCounts(notifications: readonly Notification[]): Record<NotificationView, number> {
  const counts = { inbox: 0, notifications: 0 }

  for (const notification of notifications) {
    if (isUnread(notification)) {
      counts[notification.isConversation ? 'inbox' : 'notifications']++
    }
  }

  return counts
}

/**
 * Combines live and history records, retaining the newest known read state.
 * @param {readonly Notification[]} live Live notifications.
 * @param {readonly Notification[]} history Loaded read conversations.
 * @returns {Notification[]} Unique notifications.
 */
export function mergeNotifications(live: readonly Notification[], history: readonly Notification[]): Notification[] {
  const notifications = new Map(live.map(notification => [notification.id, notification]))

  for (const notification of history) {
    const current = notifications.get(notification.id)

    if (
      !current?.seenOn ||
      (notification.seenOn && differenceInMilliseconds(notification.seenOn, current.seenOn) >= 0)
    ) {
      notifications.set(notification.id, notification)
    }
  }

  return Array.from(notifications.values())
}

/**
 * Maps notification display data and replaces snoozed originals with their reminders.
 * @param {readonly Notification[]} notifications Unique live and history records.
 * @param {readonly Task[]} tasks Current tasks selected for conversation notifications.
 * @param {User} currentUser User receiving the notifications.
 * @returns {Notification[]} Relevant notifications, newest first.
 */
export function mapListNotifications(
  notifications: readonly Notification[],
  tasks: readonly Task[],
  currentUser: User
): Notification[] {
  const taskMap = new Map(tasks.map(task => [task.id, task]))
  const rows = notifications
    .map(notification => {
      const task = notification.isConversation
        ? (taskMap.get(notification.taskId || notification.task?.id) ?? notification.task)
        : notification.task

      return new Notification({
        ...notification,
        // Notification fills missing task.project; keep query-owned tasks unchanged.
        task: task ? new Task(task) : task,
        currentUser,
        groupingType: 'no-group'
      })
    })
    .filter(notification => !!notification.groupType && (!notification.seen || notification.isConversation))
    .sort((a, b) => differenceInMilliseconds(b.createdOn, a.createdOn))
  const snoozedIds = new Set(rows.filter(row => row.reminderOfNotificationId).map(row => row.reminderOfNotificationId))

  return rows.filter(row => !snoozedIds.has(row.id))
}

/**
 * Selects the messages represented by the active view and search.
 * @param {readonly Notification[]} notifications Available notifications.
 * @param {{ sheet: boolean; view: NotificationView; showSeen: boolean; search: string }} options View, history and search selection.
 * @param options.sheet
 * @param options.view
 * @param options.showSeen
 * @param options.search
 * @returns {Notification[]} Visible messages, including messages hidden by a collapsed group.
 */
export function getVisibleNotifications(
  notifications: readonly Notification[],
  options: { sheet: boolean; view: NotificationView; showSeen: boolean; search: string }
): Notification[] {
  const search = options.search.trim().toLocaleLowerCase()

  return notifications.filter(notification => {
    let visible: boolean

    if (options.sheet) {
      visible = isUnread(notification)
    } else if (options.view === 'notifications') {
      visible = !notification.isConversation && isUnread(notification)
    } else {
      visible = notification.isConversation && (isUnread(notification) || options.showSeen)
    }

    if (!visible || !search) {
      return visible
    }

    return [
      notification.description,
      notification.entityName,
      notification.text,
      notification.detail,
      notification.project?.name,
      notification.responsibleUser?.shortName,
      notification.userInNotification?.shortName
    ]
      .filter(Boolean)
      .join(' ')
      .toLocaleLowerCase()
      .includes(search)
  })
}

/**
 * Resolves stable section identity independently of display names.
 * @param {Notification} notification Message to place.
 * @param {boolean} inbox Whether parent grouping is active.
 * @returns {string} Section identity.
 */
export function getNotificationSectionId(notification: Notification, inbox: boolean): string {
  if (!inbox || !notification.isConversation) {
    const type = notification.groupType

    if (type === 'unseen' || type === 'seen') {
      return 'conversations'
    }

    return type === 'default' || !type ? 'reminders' : type
  }

  if (!isUnread(notification)) {
    return 'seen'
  }

  const project = notification.project || notification.task?.project
  const space = notification.documentSpace || notification.document?.documentSpace

  if (project) {
    return 'project-' + project.id
  }

  if (space) {
    return 'space-' + space.id
  }

  if (notification.task?.baseType === 'private') {
    return 'task-' + notification.task.id
  }

  return notification.document?.isPrivate ? 'private-documents' : 'conversations'
}

/**
 * Resolves the display data for one section.
 * @param {Notification} notification First message in the section.
 * @param {boolean} inbox Whether parent grouping is active.
 * @param {NotificationTranslations} translations Notification copy.
 * @returns {SectionHeading} Section heading.
 */
function getSectionHeading(
  notification: Notification,
  inbox: boolean,
  translations: NotificationTranslations
): SectionHeading {
  const id = getNotificationSectionId(notification, inbox)

  if (!inbox) {
    const category = id as NotificationCategory

    return { id, label: translations.sections[category], icon: CATEGORY_ICONS[category] }
  }

  if (id === 'seen') {
    return { id, label: translations.seenMessages, icon: 'message-circle' }
  }

  const project = notification.project || notification.task?.project
  const space = notification.documentSpace || notification.document?.documentSpace
  let label: string = translations.sections.conversations

  if (project) {
    label = project.name
  } else if (space) {
    label = space.name
  } else if (notification.task?.baseType === 'private') {
    label = notification.task.name
  } else if (notification.document?.isPrivate) {
    label = translations.privateDocuments
  }

  return { id, label, project, icon: project ? 'folder' : 'message-circle' }
}

/**
 * Groups a conversation behind its newest message while retaining the full action scope.
 * @param {NotificationSection} section Parent section.
 * @param {ReadonlySet<string>} expanded Expanded conversation identities.
 * @returns {NotificationRow[]} Rendered rows and the notifications each action represents.
 */
function getInboxRows(section: NotificationSection, expanded: ReadonlySet<string>): NotificationRow[] {
  const groups = new Map<string, Notification[]>()

  for (const notification of section.notifications) {
    const id = notification.entityType + '-' + (notification.entityId || notification.id)
    const group = groups.get(id) ?? []

    group.push(notification)
    groups.set(id, group)
  }

  return Array.from(groups, ([id, notifications]) => {
    const groupId = section.id + '-' + id
    const isExpanded = expanded.has(groupId)
    const visible = isExpanded ? notifications : notifications.slice(0, 1)

    return visible.map((notification, index) => ({
      notification,
      groupId: notifications.length > 1 && index === 0 ? groupId : undefined,
      groupCount: notifications.length,
      expanded: isExpanded,
      groupPosition: getGroupPosition(index, visible.length),
      actionNotifications: isExpanded ? [notification] : notifications
    }))
  }).flat()
}

/**
 * Finds a row's place inside an expanded conversation.
 * @param {number} index Row index inside the conversation.
 * @param {number} count Number of visible rows in the conversation.
 * @returns {NotificationRow['groupPosition']} Position, or undefined for a single row.
 */
function getGroupPosition(index: number, count: number): NotificationRow['groupPosition'] {
  if (count < 2) {
    return undefined
  }

  if (index === 0) {
    return 'first'
  }

  return index === count - 1 ? 'last' : 'middle'
}

/**
 * Builds ordered sections and their action scopes in one grouping pass.
 * @param {readonly Notification[]} notifications Messages matching the active view and search.
 * @param {object} options Parent grouping, expansion, pending actions and translated headings.
 * @param options.inbox
 * @param options.search
 * @param options.expanded
 * @param options.pending
 * @param options.translations
 * @returns {NotificationSection[]} Sections ready for rendering.
 */
export function getNotificationSections(
  notifications: readonly Notification[],
  options: {
    inbox: boolean
    search: string
    expanded: ReadonlySet<string>
    pending: ReadonlySet<string>
    translations: NotificationTranslations
  }
): NotificationSection[] {
  const groups = new Map<string, NotificationSection>()

  for (const notification of notifications) {
    const id = getNotificationSectionId(notification, options.inbox)
    let section = groups.get(id)

    if (!section) {
      const heading = getSectionHeading(notification, options.inbox, options.translations)

      section = {
        ...heading,
        markReadLabel: replace(options.translations.markSectionAsSeen, { group: heading.label }),
        startIndex: 0,
        notifications: [],
        rows: [],
        pending: false,
        hasUnseen: false
      }
      groups.set(id, section)
    }

    section.notifications.push(notification)
    section.hasUnseen ||= isUnread(notification)
    section.pending ||= options.pending.has(notification.id)
  }

  let ids: string[]

  if (options.inbox) {
    ids = [...groups.keys()].filter(id => id !== 'private-documents' && id !== 'seen')
    ids.push('private-documents', 'seen')
  } else {
    ids = Object.keys(CATEGORY_ICONS)
  }

  const sections = ids.map(id => groups.get(id)).filter((section): section is NotificationSection => !!section)
  let startIndex = 0

  for (const section of sections) {
    section.rows =
      options.inbox && section.id !== 'seen' && !options.search.trim()
        ? getInboxRows(section, options.expanded)
        : section.notifications.map(notification => ({ notification, actionNotifications: [notification] }))
    section.startIndex = startIndex
    startIndex += section.rows.length
  }

  return sections
}
